import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  type MiniAppInfo,
  getInstalledApps,
  installApp,
  uninstallApp,
  getClient,
} from '../miniApps';

export function StorePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [apps, setApps] = useState<MiniAppInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [installedIds, setInstalledIds] = useState<Set<string>>(new Set());
  const [installing, setInstalling] = useState<Set<string>>(new Set());

  const refreshInstalled = useCallback(() => {
    setInstalledIds(new Set(getInstalledApps().map(a => a.app_id)));
  }, []);

  const loadApps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClient().fetchApps('published');
      setApps(data);
      refreshInstalled();
    } catch (err: any) {
      setError(err.message || t('store.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [refreshInstalled]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  const handleInstall = async (app: MiniAppInfo) => {
    setInstalling(prev => new Set(prev).add(app.app_id));
    try {
      const bundleRes = await fetch(await getClient().getBundleUrl(app.app_id));
      if (!bundleRes.ok) throw new Error('Bundle not available');

      installApp({
        app_id: app.app_id,
        name: app.name,
        description: app.description,
        icon: app.icon,
        version: app.version,
      });
      refreshInstalled();
    } catch (err: any) {
      alert(t('common.installFailed', { message: err.message }));
    } finally {
      setInstalling(prev => {
        const next = new Set(prev);
        next.delete(app.app_id);
        return next;
      });
    }
  };

  const handleUninstall = (appId: string) => {
    uninstallApp(appId);
    refreshInstalled();
  };

  return (
    <div className="mx-auto max-w-4xl p-6 space-y-6">
      <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="text-3xl">🏪</span>
          <h1 className="text-2xl font-bold text-gray-900">{t('store.title')}</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/store/settings')}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
            title={t('common.settings')}
          >
            ⚙️
          </button>
          <button
            onClick={loadApps}
            disabled={loading}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          {t('common.loading')}
        </div>
      ) : apps.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-gray-400">
          <span className="text-5xl mb-3">📭</span>
          <p>{t('store.noApps')}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {apps.map(app => {
            const installed = installedIds.has(app.app_id);
            const busy = installing.has(app.app_id);
            return (
              <div
                key={app.app_id}
                className="rounded-xl bg-white p-5 shadow-sm border border-gray-100 flex flex-col"
              >
                <div className="flex items-start gap-3">
                  <span className="text-3xl">{iconEmoji(app.icon)}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{app.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">v{app.version}</p>
                  </div>
                </div>
                {app.description && (
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">{app.description}</p>
                )}
                <div className="mt-auto pt-4 flex gap-2">
                  {installed ? (
                    <>
                      <button
                        onClick={() => navigate(`/app/${app.app_id}`)}
                        className="flex-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                      >
                        {t('common.open')}
                      </button>
                      <button
                        onClick={() => handleUninstall(app.app_id)}
                        className="rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                      >
                        {t('common.uninstall')}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleInstall(app)}
                      disabled={busy}
                      className="flex-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {busy ? t('common.installing') : t('common.install')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function iconEmoji(icon: string | null): string {
  const map: Record<string, string> = {
    smile: '😊',
    camera: '📷',
    qrcode: '📱',
    video: '📹',
    puzzle: '🧩',
  };
  return map[icon ?? ''] ?? '🧩';
}
